import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useDataProvider, useNotify, useGetList } from "ra-core";
import { usePipelines } from "../deals/usePipelines";
import type { Goal, Sale, DealPipeline, DealStage } from "../types";

interface GoalDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  goalToEdit?: Goal | null;
}

export const GoalDialog: React.FC<GoalDialogProps> = ({
  open,
  onClose,
  onSuccess,
  goalToEdit,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { pipelines, currentPipelineId } = usePipelines();

  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });

  const [name, setName] = useState("");
  const [pipelineId, setPipelineId] = useState(
    currentPipelineId || "principal",
  );
  const [stage, setStage] = useState("");
  const [scope, setScope] = useState<"etapa" | "usuario">("etapa");
  const [salesId, setSalesId] = useState<string>("");
  const [metricType, setMetricType] = useState<"volume" | "value">("volume");
  const [targetValue, setTargetValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodPreset, setPeriodPreset] = useState<
    "semanal" | "mensal" | "trimestral" | "personalizado"
  >("mensal");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Obtém as etapas do funil selecionado
  const activePipeline: DealPipeline | undefined =
    pipelines.find((p) => p.id === pipelineId) || pipelines[0];
  const stages: DealStage[] = activePipeline?.etapas || [];

  // Atalhos de período (Semanal, Mensal, Trimestral)
  const applyPeriodPreset = (
    preset: "semanal" | "mensal" | "trimestral" | "personalizado",
  ) => {
    setPeriodPreset(preset);
    const now = new Date();
    const toYMD = (d: Date) => d.toISOString().split("T")[0];

    if (preset === "semanal") {
      const day = now.getDay();
      const diffToMonday = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now.setDate(diffToMonday));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      setStartDate(toYMD(monday));
      setEndDate(toYMD(sunday));
    } else if (preset === "mensal") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(toYMD(firstDay));
      setEndDate(toYMD(lastDay));
    } else if (preset === "trimestral") {
      const quarter = Math.floor(now.getMonth() / 3);
      const firstDay = new Date(now.getFullYear(), quarter * 3, 1);
      const lastDay = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
      setStartDate(toYMD(firstDay));
      setEndDate(toYMD(lastDay));
    }
  };

  // Inicializa valores ao abrir
  useEffect(() => {
    if (goalToEdit) {
      setName(goalToEdit.name);
      setPipelineId(goalToEdit.pipeline_id || currentPipelineId || "principal");
      setStage(goalToEdit.stage);
      setScope(goalToEdit.scope);
      setSalesId(goalToEdit.sales_id ? String(goalToEdit.sales_id) : "");
      setMetricType(goalToEdit.metric_type);
      setTargetValue(String(goalToEdit.target_value));
      setStartDate(goalToEdit.start_date);
      setEndDate(goalToEdit.end_date);
      setPeriodPreset("personalizado");
    } else {
      setName("");
      const initialPipeId =
        currentPipelineId || pipelines[0]?.id || "principal";
      setPipelineId(initialPipeId);
      const initialPipeline =
        pipelines.find((p) => p.id === initialPipeId) || pipelines[0];
      setStage(initialPipeline?.etapas[0]?.value || "prospeccao");
      setScope("etapa");
      setSalesId("");
      setMetricType("volume");
      setTargetValue("20");
      applyPeriodPreset("mensal");
    }
  }, [goalToEdit, open, currentPipelineId, pipelines]);

  // Se trocar de funil, seleciona a primeira etapa válida
  const handlePipelineChange = (newPipeId: string) => {
    setPipelineId(newPipeId);
    const pipe = pipelines.find((p) => p.id === newPipeId);
    if (pipe && pipe.etapas.length > 0) {
      setStage(pipe.etapas[0].value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !targetValue ||
      isNaN(Number(targetValue)) ||
      Number(targetValue) <= 0
    ) {
      notify("Informe um valor-alvo válido maior que zero.", {
        type: "warning",
      });
      return;
    }
    if (!startDate || !endDate) {
      notify("Informe o período de início e fim da meta.", { type: "warning" });
      return;
    }
    if (scope === "usuario" && !salesId) {
      notify("Selecione um vendedor para a meta individual.", {
        type: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedStageObj = stages.find((s) => s.value === stage);
      const stageName = selectedStageObj?.label || stage;
      const finalName =
        name.trim() ||
        `Meta ${scope === "etapa" ? "Equipe" : "Individual"} - ${stageName} (${metricType === "volume" ? "Volume" : "Valor"})`;

      const payload = {
        name: finalName,
        pipeline_id: pipelineId,
        stage: stage,
        scope: scope,
        sales_id: scope === "usuario" && salesId ? Number(salesId) : null,
        metric_type: metricType,
        target_value: Number(targetValue),
        start_date: startDate,
        end_date: endDate,
      };

      if (goalToEdit) {
        await dataProvider.update("goals", {
          id: goalToEdit.id,
          data: payload,
          previousData: goalToEdit,
        });
        notify("Meta atualizada com sucesso!", { type: "success" });
      } else {
        await dataProvider.create("goals", {
          data: payload,
        });
        notify("Meta criada com sucesso!", { type: "success" });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar meta:", err);
      notify("Erro ao salvar meta: " + (err.message || "Tente novamente"), {
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {goalToEdit ? "Editar Meta Comercial" : "Criar Nova Meta Comercial"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Configure o escopo, a etapa do funil e o valor-alvo para acompanhar
            o progresso em tempo real.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Nome da Meta (opcional / sugerido) */}
          <div className="space-y-1">
            <Label htmlFor="goalName" className="text-xs font-medium">
              Nome da Meta (opcional)
            </Label>
            <Input
              id="goalName"
              placeholder="Ex: Meta Propostas Outubro, 30 Leads..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="text-xs h-8.5"
            />
          </div>

          {/* Funil e Etapa */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Funil de Vendas</Label>
              <Select value={pipelineId} onValueChange={handlePipelineChange}>
                <SelectTrigger className="h-8.5 text-xs">
                  <SelectValue placeholder="Selecione o funil" />
                </SelectTrigger>
                <SelectContent>
                  {pipelines.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">
                      {p.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Etapa do Funil</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="h-8.5 text-xs">
                  <SelectValue placeholder="Selecione a etapa" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem
                      key={s.value}
                      value={s.value}
                      className="text-xs"
                    >
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Escopo: Etapa (Equipe Toda) ou Usuário */}
          <div className="space-y-1.5 p-3 rounded-lg border bg-muted/20">
            <Label className="text-xs font-medium">Escopo da Meta</Label>
            <RadioGroup
              value={scope}
              onValueChange={(val: any) => setScope(val)}
              className="flex gap-4 pt-1"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="etapa" id="scope-etapa" />
                <Label
                  htmlFor="scope-etapa"
                  className="text-xs font-normal cursor-pointer"
                >
                  Etapa do Funil (Toda a equipe)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="usuario" id="scope-usuario" />
                <Label
                  htmlFor="scope-usuario"
                  className="text-xs font-normal cursor-pointer"
                >
                  Vendedor específico
                </Label>
              </div>
            </RadioGroup>

            {/* Seletor de Vendedor quando Escopo = usuario */}
            {scope === "usuario" && (
              <div className="pt-2 space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Selecionar Colaborador
                </Label>
                <Select value={salesId} onValueChange={setSalesId}>
                  <SelectTrigger className="h-8.5 text-xs bg-background">
                    <SelectValue placeholder="Selecione o vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {(salesList || []).map((s) => (
                      <SelectItem
                        key={s.id}
                        value={String(s.id)}
                        className="text-xs"
                      >
                        {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                          s.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Tipo de Métrica e Valor-Alvo */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Tipo da Meta</Label>
              <RadioGroup
                value={metricType}
                onValueChange={(val: any) => setMetricType(val)}
                className="space-y-1.5 pt-0.5"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="volume" id="metric-volume" />
                  <Label
                    htmlFor="metric-volume"
                    className="text-xs font-normal cursor-pointer"
                  >
                    Por Ganho (Qtd. de negócios)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="value" id="metric-value" />
                  <Label
                    htmlFor="metric-value"
                    className="text-xs font-normal cursor-pointer"
                  >
                    Por Valor (Soma em R$)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-1">
              <Label htmlFor="targetValue" className="text-xs font-medium">
                Valor-Alvo {metricType === "value" ? "(R$)" : "(Qtd)"}
              </Label>
              <Input
                id="targetValue"
                type="number"
                step={metricType === "value" ? "0.01" : "1"}
                min="1"
                placeholder={metricType === "value" ? "50000" : "30"}
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                required
                className="text-xs h-8.5 font-semibold"
              />
              <span className="text-[10px] text-muted-foreground">
                {metricType === "value"
                  ? "Soma total do valor dos negócios fechados"
                  : "Número total de leads atingidos"}
              </span>
            </div>
          </div>

          {/* Período com Atalhos */}
          <div className="space-y-2 p-3 rounded-lg border bg-muted/20">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Período de Vigência</Label>
              {/* Atalhos Rápidos */}
              <div className="flex gap-1">
                {(
                  ["semanal", "mensal", "trimestral", "personalizado"] as const
                ).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyPeriodPreset(preset)}
                    className={`text-[10px] px-2 py-0.5 rounded-md font-medium transition-colors ${
                      periodPreset === preset
                        ? "bg-primary text-primary-foreground shadow-2xs"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                  >
                    {preset.charAt(0).toUpperCase() + preset.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <div className="space-y-1">
                <Label
                  htmlFor="startDate"
                  className="text-[11px] text-muted-foreground"
                >
                  Início
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPeriodPreset("personalizado");
                  }}
                  required
                  className="text-xs h-8 bg-background"
                />
              </div>

              <div className="space-y-1">
                <Label
                  htmlFor="endDate"
                  className="text-[11px] text-muted-foreground"
                >
                  Término
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPeriodPreset("personalizado");
                  }}
                  required
                  className="text-xs h-8 bg-background"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
              className="text-xs"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="text-xs gap-1.5"
            >
              {isSubmitting
                ? "Salvando..."
                : goalToEdit
                  ? "Salvar Alterações"
                  : "Criar Meta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
